/**
 * Write-side companion to a server data grid: runs a resource's create/patch/
 * remove mutations with shared busy + error state, snackbar reporting, and a
 * grid refresh on success. The mutation functions are whatever the resource
 * module exposes (RTK Query triggers, or a plain api fn during migration) and
 * must reject on failure. Failures are normalized through parseApiError so
 * field errors can feed a dialog form and form-wide messages hit the snackbar.
 */
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { addToast } from '@sophocles/features/toast';
import { parseApiError } from '@sophocles/utils/apiErrors';

const EMPTY_ERROR = { fieldErrors: {}, nonFieldMessages: [] };

export default function useGridCrud({ gridState, create, patch, remove, messages = {}, knownFields }) {
  const dispatch = useDispatch();
  const [isMutating, setIsMutating] = useState(false);
  const [mutationError, setMutationError] = useState(EMPTY_ERROR);

  const resetError = useCallback(() => setMutationError(EMPTY_ERROR), []);

  const run = useCallback(
    async (mutate, successMessage) => {
      setIsMutating(true);
      setMutationError(EMPTY_ERROR);
      try {
        const result = await mutate();
        if (successMessage) dispatch(addToast({ message: successMessage, level: 'success' }));
        gridState?.refresh?.();
        return { ok: true, result };
      } catch (error) {
        const parsed = parseApiError(error, { knownFields });
        setMutationError(parsed);
        parsed.nonFieldMessages.forEach(message => dispatch(addToast({ message, level: 'error' })));
        return { ok: false, error: parsed };
      } finally {
        setIsMutating(false);
      }
    },
    [dispatch, gridState, knownFields],
  );

  const createRow = useCallback(body => run(() => create(body), messages.created), [run, create, messages.created]);
  const patchRow = useCallback(
    (id, body) => run(() => patch(id, body), messages.updated),
    [run, patch, messages.updated],
  );
  const removeRow = useCallback(id => run(() => remove(id), messages.removed), [run, remove, messages.removed]);

  return useMemo(
    () => ({ createRow, patchRow, removeRow, isMutating, mutationError, resetError }),
    [createRow, patchRow, removeRow, isMutating, mutationError, resetError],
  );
}
